import { ContactType } from '@api/generated/graphql';
import { layoutLocators } from './layout';
import { AddressVerificationStatuses } from '@components/forms/components/AddressFormManager/components/AddressVerificationStatus/AddressVerificationDefinitions';
import { merge } from 'lodash';

const customerSearchIds = {
  searchResults: 'Inbox-CustomerSearch-SearchResults',
  searchBtn: 'Inbox-CustomerSearch-Button',
  searchTitle: 'Inbox-CustomerSearch-Title',
  searchForm: {
    phone: 'Inbox-CustomerSearch-PhoneInput',
    name: 'Inbox-CustomerSearch-NameInput',
    street: 'Inbox-CustomerSearch-StreetInput',
    zip: 'Inbox-CustomerSearch-ZipInput',
  },
  customerRefreshButton: 'Inbox-CustomerSearch-Refresh-Button',
  customerLoadingSpinner: 'Inbox-CustomerSearch-Loading-Spinner',
  customerTag: 'Inbox-CustomerSearch-TenantId-Tag',
  customerLink: 'Inbox-CustomerSearch-TenantId-CustomerId-',
  contactMethods: {
    email: 'Inbox-CustomerSearch-EmailContactMethodsField',
    phone: 'Inbox-CustomerSearch-PhoneContactMethodsField',
  },
  newCustomerBtn: 'Inbox-CustomerSearch-NewCustomerBtn',
  taskRow: 'Inbox-TaskList-TaskRow',
  createFormFields: {
    locationName: 'Inbox-CreateCustomer-LocationNameInput',
    searchForAnAddress: 'Inbox-CreateCustomer-SearchForAnAddressInput',
    enterAddressManually: 'Inbox-CreateCustomer-EnterAddressManuallyBtn',
    address: {
      street: 'Inbox-CreateCustomer-AddressStreetInput',
      unit: 'Inbox-CreateCustomer-AddressUnitInput',
      city: 'Inbox-CreateCustomer-AddressCityInput',
      state: 'Inbox-CreateCustomer-AddressStateInput',
      country: 'Inbox-CreateCustomer-AddressCountryInput',
      zip: 'Inbox-CreateCustomer-AddressZipInput',
      verifyAddressLabels: {
        [AddressVerificationStatuses.VERIFIED]: 'Inbox-CreateCustomer-Verified',
        [AddressVerificationStatuses.NO_RESULTS]:
          'Inbox-CreateCustomer-NoResults',
        [AddressVerificationStatuses.NOT_VERIFIED]:
          'Inbox-CreateCustomer-VerifyAddressBtn',
      },
    },
    addContactMethod: 'Inbox-CreateCustomer-AddContactMethodBtn',
    createSubmitBtn: 'Inbox-CreateCustomer-SubmitSaveBtn',
  },
  editFormFields: {
    [ContactType.Email]: 'Inbox-CustomerSearch-EditFormEmail',
    [ContactType.Fax]: 'Inbox-CustomerSearch-EditFormFax',
    [ContactType.MobilePhone]: 'Inbox-CustomerSearch-EditFormMobilePhone',
    [ContactType.Phone]: 'Inbox-CustomerSearch-EditFormPhone',
    [ContactType.Unknown]: 'Inbox-CustomerSearch-EditFormUnknown',
  },
  customerLocation: 'Inbox-CustomerSearch-Location',
  submitSaveBtn: 'Inbox-CustomerSearch-SubmitSaveBtn',
  submitCancelBtn: 'Inbox-CustomerSearch-SubmitCancelBtn',
  editIconBtn: 'Inbox-CustomerSearch-EditIconBtn',
  failedTenantsAlert: 'Inbox-CustomerSearch-FailedTenants-Alert',
  contactMethodPhoneTypeMenu: 'Inbox-CustomerSearch-ContactMethodIconMenu',
  contactMethodPhoneTypeMenuBtn:
    'Inbox-CustomerSearch-ContactMethodIconMenu-Button',
} as const;

const customerSearchTestIds = {
  outboundConfirmationModalSelectedTenant:
    'Inbox-ConfirmationModal-SelectedTenant',
} as const;

const callBarIds = {
  dragHandle: 'Inbox-CallBar-DragHandle',
  callControls: 'Inbox-CallBar-CallControls',
  endCallButton: 'Inbox-CallBar-EndCallButton',
  toggleCallType: 'Inbox-CallBar-ToggleCallType',
  searchInputUser: 'Inbox-CallBar-SearchInputUser',
  usersListTableToCall: 'Inbox-CallBar-UsersListTableToCall',
  usersListRowSelectToCall: 'Inbox-CallBar-UsersListRowSelectToCall',
  tasksListTableCall: 'Inbox-CallBar-TasksListTableCall',
  tasksListRowSelectCall: 'Inbox-CallBar-TasksListRowSelectCall',
  displayNameChecker: 'Inbox-CallBar-DisplayNameChecker',
  durationLabel: 'Inbox-CallBar-DurationLabel',
  keypadBtn: 'Inbox-CallBar-KeypadBtn',
  audioDeviceBtn: 'Inbox-CallBar-AudioDeviceBtn',
  muteBtn: 'Inbox-CallBar-MuteButton',
  unmuteBtn: 'Inbox-CallBar-UnmuteButton',
  placeCallOnHoldBtn: 'Inbox-CallBar-PlaceCallOnHoldBtn',
  resumeCallBtn: 'Inbox-CallBar-ResumeCallBtn',
  pauseRecordingAudioCallBtn: 'Inbox-CallBar-PauseRecordingAudioCallBtn',
  transferBtn: 'Inbox-CallBar-TransferBtn',
  transferModal: 'Inbox-CallBar-TransferModal',
  transferConfirmationModal: 'Inbox-CallBar-TransferConfirmationModal',
  transferConfirmationModalBackBtn:
    'Inbox-CallBar-TransferConfirmationModalBackBtn',
  transferConfirmationModalTransferBtn:
    'Inbox-CallBar-TransferConfirmationModalTransferBtn',
  transferConfirmationModalCallFirstBtn:
    'Inbox-CallBar-TransferConfirmationModalCallFirstBtn',
  completeTransferBtn: 'CompleteTransfer-Button',
} as const;

const callBarTestIds = {
  muteBtnTooltip: 'Inbox-CallBar-MuteButtonTooltip',
  unmuteBtnTooltip: 'Inbox-CallBar-UnmuteButtonTooltip',
  placeCallOnHoldBtnTooltip: 'Inbox-CallBar-PlaceCallOnHoldBtnTooltip',
  pauseRecordingAudioCallTooltip:
    'Inbox-CallBar-PauseRecordingAudioCallTooltip',
  transferBtnTooltip: 'Inbox-CallBar-TransferBtnTooltip',
  resumeCallBtnTooltip: 'Inbox-CallBar-ResumeCallBtnTooltip',
  outboundCallModalSelectedTenant:
    'Inbox-CallBar-OutboundCallModal-SelectedTenant',
  agentCallerIdLoader: 'AgentCallerId-Loader',
  agentCallerIdName: 'AgentCallerId-Name',
  agentCallerIdNumber: 'AgentCallerId-Number',
  agentCallerIdChangeButton: 'AgentCallerId-ChangeButton',
} as const;

const taskList = {
  callFrom: 'Inbox-TaskList-CallFrom',
  acceptButton: 'Inbox-TaskList-AcceptButton',
  closeButton: 'Inbox-TaskList-CloseButton',
  rejectButton: 'Inbox-TaskList-RejectButton',
  dismissButton: 'Inbox-TaskList-DismissButton',
  missedCallIcon: 'Inbox-TaskList-MissedCallIcon',
  taskTag: 'Inbox-TaskList-TaskTag',
  displayName: 'Inbox-TaskList-DisplayName',
  timeDisplay: 'Inbox-TaskList-TimeDisplay',
  timeoutCountdown: 'Inbox-TaskList-TimeoutCountdown',
  tenantTag: 'Inbox-TaskList-TenantTag',
  header: 'Inbox-TaskList-Header',
  campaignName: 'Inbox-TaskList-CampaignName',
  workflowName: 'Inbox-TaskList-WorkflowName',
  resumeButton: 'Inbox-TaskList-ResumeButton',
  claimTaskButton: 'Inbox-TaskList-ClaimTaskButton',
  speedToLeadCallButton: 'Inbox-TaskList-SpeedToLeadCallButton',
  speedToLeadLogOutcomeSubmit: 'Inbox-TaskList-SpeedToLeadLogOutcomeSubmit',
} as const;

const callClassificationModal = {
  modalContent: 'Inbox-CallClassification-Modal',
  goBackButton: 'Inbox-CallClassification-GoBackButton',
  closeAndClassifyButton: 'Inbox-CallClassification-CloseAndClassifyButton',
  reasonFormSelect: 'Inbox-CallClassification-ReasonFormSelect',
  notesInput: 'Inbox-CallClassification-NotesInput',
};

export const inboxIdLocators = {
  customerSearch: customerSearchIds,
  callBar: callBarIds,
  taskList,
  callClassificationModal,
  ...layoutLocators,
};

export const inboxTestIdLocators = {
  callBar: callBarTestIds,
  customerSearch: customerSearchTestIds,
};

export const inboxLocators = merge({}, inboxIdLocators, inboxTestIdLocators);
